import { Typography, Card, Row, Col, Modal, Form, Input, Button, Switch } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useState, useEffect, useRef } from 'react';
import './singleSiteCard.css';
import OnlineStatusIndicator from '../../Status/OnlineStatusIndicator';
import UsersIcon from './../../../../assets/svgs/IconsWhite/UsersWhiteIcon.svg';
import useAxiosPost from '../../../../hooks/useAxiosPost';
import useMultiNotification from '../../../../hooks/useNotification';
import { getUserType } from '../../../../helpers/userDetails';

const SingleSiteMidCard: React.FC<SingleSiteMidCardProp> = ({ title, value, icon, cardClassName }) => {
  return (
    <div className={`single-site-mid-card ${cardClassName}`}>
      <div className="mid-card-title-container">
        <img src={icon} />
        <Typography className="mid-card-box-title">{title}</Typography>
      </div>
      <div className="mid-card-box-value-container">
        <Typography className="mid-card-box-value">{value}</Typography>
      </div>
    </div>
  );
};

const RouterSingleRow: React.FC<SingleRowRouterDetailsProp> = ({ title, value, valueSpecialClass }) => {
  return (
    <Row gutter={[6, 16]}>
      <Col span={4}>
        <div className="router-details-label-container">
          <Typography className="router-details-label">{title}</Typography>
          <Typography className="router-details-label">:</Typography>
        </div>
      </Col>
      <Col span={20}>
        <Typography className={`router-details-value ${valueSpecialClass}`}>{value}</Typography>
      </Col>
    </Row>
  );
};
interface SingleSiteCardWithEditProps extends SingleSiteCardProp {
  onUpdate?: () => void;
}

const SingleSiteCard: React.FC<SingleSiteCardWithEditProps> = ({
  enable,
  intPkCompanyID,
  intPkCompanySiteID,
  siteAddress,
  siteName,
  routerDetails,
  onlineUsersCount = 0,
  onUpdate,
}) => {
  const { openNotification, contextHolder } = useMultiNotification();
  const [form] = Form.useForm();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isToggleConfirmOpen, setIsToggleConfirmOpen] = useState(false);
  const [pendingToggleValue, setPendingToggleValue] = useState<number | null>(null);
  const [switchChecked, setSwitchChecked] = useState<boolean>(Boolean(enable));
  const hasProcessedUpdateSuccess = useRef(false);
  const lastProcessedUpdateErrorCount = useRef(0);
  const lastProcessedToggleErrorCount = useRef(0);
  const hasProcessedToggleSuccess = useRef(false);

  // Update switch state when enable prop changes
  useEffect(() => {
    setSwitchChecked(Boolean(enable));
  }, [enable]);

  const [
    updateSiteData,
    updateSiteResponse,
    updateSiteError,
    updateSiteLoading,
    updateSiteMessage,
    updateSiteErrorCount,
  ] = useAxiosPost<ResponseProp>('company-sites/update');

  const [
    toggleStatusData,
    toggleStatusResponse,
    toggleStatusError,
    toggleStatusLoading,
    toggleStatusMessage,
    toggleStatusErrorCount,
  ] = useAxiosPost<ResponseProp>('company-sites/toggle-status');

  // Set form initial values when modal opens
  useEffect(() => {
    if (isEditModalOpen) {
      form.setFieldsValue({
        siteName,
        siteAddress,
      });
    }
  }, [isEditModalOpen, siteName, siteAddress, form]);

  // Reset flags when response becomes null (new request starting)
  useEffect(() => {
    if (updateSiteResponse === null) {
      hasProcessedUpdateSuccess.current = false;
    }
  }, [updateSiteResponse]);

  useEffect(() => {
    if (toggleStatusResponse === null) {
      hasProcessedToggleSuccess.current = false;
    }
  }, [toggleStatusResponse]);

  // Handle update success
  useEffect(() => {
    console.log('=== UPDATE SITE EFFECT ===');
    console.log('updateSiteResponse:', updateSiteResponse);
    console.log('hasProcessedUpdateSuccess:', hasProcessedUpdateSuccess.current);

    if (updateSiteResponse !== null && !hasProcessedUpdateSuccess.current) {
      console.log('✅ SUCCESS: Showing site update notification!');
      hasProcessedUpdateSuccess.current = true;
      openNotification('success', 'Site Updated', 'Site details updated successfully');
      form.resetFields();
      if (onUpdate) {
        setTimeout(() => {
          onUpdate();
          setIsEditModalOpen(false);
        }, 2000);
      }
    }
  }, [updateSiteResponse, openNotification, form, onUpdate]);

  // Handle update error
  useEffect(() => {
    console.log('Update error check:', updateSiteError, updateSiteErrorCount);
    if (
      updateSiteError !== null &&
      updateSiteErrorCount !== 0 &&
      lastProcessedUpdateErrorCount.current !== updateSiteErrorCount
    ) {
      console.log('ERROR: Showing error notification');
      lastProcessedUpdateErrorCount.current = updateSiteErrorCount;
      openNotification('error', 'Failed to Update Site', updateSiteError);
    }
  }, [updateSiteError, updateSiteErrorCount, openNotification]);

  // Handle toggle status success
  useEffect(() => {
    console.log('=== TOGGLE STATUS EFFECT ===');
    console.log('toggleStatusResponse:', toggleStatusResponse);
    console.log('hasProcessedToggleSuccess:', hasProcessedToggleSuccess.current);

    if (toggleStatusResponse !== null && !hasProcessedToggleSuccess.current) {
      console.log('✅ SUCCESS: Showing toggle status notification!');
      hasProcessedToggleSuccess.current = true;
      const responseData: any = toggleStatusResponse.responseData;
      const newEnable = responseData?.enable || 0;
      setSwitchChecked(Boolean(newEnable));
      openNotification('success', 'Status Updated', `Site ${newEnable === 1 ? 'enabled' : 'disabled'} successfully`);
      setPendingToggleValue(null);
      if (onUpdate) {
        setTimeout(() => {
          onUpdate();
          setIsToggleConfirmOpen(false);
        }, 2000);
      }
    }
  }, [toggleStatusResponse, openNotification, onUpdate]);

  // Handle toggle status error
  useEffect(() => {
    if (
      toggleStatusError !== null &&
      toggleStatusErrorCount !== 0 &&
      lastProcessedToggleErrorCount.current !== toggleStatusErrorCount
    ) {
      lastProcessedToggleErrorCount.current = toggleStatusErrorCount;
      openNotification('error', 'Failed to Update Status', toggleStatusError);
      setIsToggleConfirmOpen(false);
      setPendingToggleValue(null);
      // Revert switch to original state
      setSwitchChecked(Boolean(enable));
    }
  }, [toggleStatusError, toggleStatusErrorCount, openNotification, enable]);

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleCancel = () => {
    if (!updateSiteLoading) {
      setIsEditModalOpen(false);
      form.resetFields();
    }
  };

  const handleUpdate = (values: any) => {
    console.log('🚀 handleUpdate called with values:', values);
    const { siteName: newSiteName, siteAddress: newSiteAddress } = values;
    console.log('📤 Calling updateSiteData API...');
    updateSiteData({
      id: intPkCompanySiteID,
      siteName: newSiteName.trim(),
      siteAddress: newSiteAddress.trim(),
    } as any);
  };

  const handleModalClose = () => {
    if (!updateSiteLoading) {
      handleCancel();
    }
  };

  const handleToggleChange = (checked: boolean) => {
    const newValue = checked ? 1 : 0;
    setPendingToggleValue(newValue);
    setIsToggleConfirmOpen(true);
    // Don't update switch state yet - wait for confirmation
  };

  const handleToggleConfirm = () => {
    if (pendingToggleValue !== null) {
      toggleStatusData({
        id: intPkCompanySiteID,
        enable: pendingToggleValue,
      } as any);
    }
  };

  const handleToggleCancel = () => {
    setIsToggleConfirmOpen(false);
    setPendingToggleValue(null);
    // Revert switch to original state
    setSwitchChecked(Boolean(enable));
  };

  const userRole = getUserType();
  const canEditSite = userRole === 'ADMIN';

  return (
    <>
      {contextHolder}
      <Modal
        title="Update Site Details"
        open={isEditModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        maskClosable={!updateSiteLoading}
        closable={!updateSiteLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdate} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Site Name</span>}
            name="siteName"
            rules={[
              {
                required: true,
                message: 'Please enter site name',
              },
              {
                min: 1,
                message: 'Site name cannot be empty',
              },
              {
                max: 100,
                message: 'Site name must be less than 100 characters',
              },
              {
                whitespace: true,
                message: 'Site name cannot be only spaces',
              },
            ]}
          >
            <Input placeholder="Enter site name" disabled={updateSiteLoading} maxLength={100} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Site Address</span>}
            name="siteAddress"
            rules={[
              {
                required: true,
                message: 'Please enter site address',
              },
              {
                min: 1,
                message: 'Site address cannot be empty',
              },
              {
                max: 200,
                message: 'Site address must be less than 200 characters',
              },
              {
                whitespace: true,
                message: 'Site address cannot be only spaces',
              },
            ]}
          >
            <Input.TextArea
              placeholder="Enter site address"
              rows={3}
              disabled={updateSiteLoading}
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCancel} style={{ marginRight: '8px' }} disabled={updateSiteLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={updateSiteLoading} disabled={updateSiteLoading}>
              {updateSiteLoading ? 'Updating...' : 'Update Site'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Confirm Status Change"
        open={isToggleConfirmOpen}
        onOk={handleToggleConfirm}
        onCancel={handleToggleCancel}
        confirmLoading={toggleStatusLoading}
        okText="Confirm"
        cancelText="Cancel"
        width={400}
      >
        <Typography>Are you sure you want to {pendingToggleValue === 1 ? 'enable' : 'disable'} this site?</Typography>
      </Modal>

      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card className="border-card">
            <div className="site-card-top-items">
              <div className="site-details-box">
                <div className="site-title-box">
                  <Typography className="site-header-title">{siteName}</Typography>
                  {canEditSite && (
                    <EditOutlined
                      className="cursor-pointer edit-site-icon"
                      onClick={handleEditClick}
                      style={{ fontSize: '18px', color: '#3A77BC' }}
                    />
                  )}
                </div>
                <div style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  <Typography className="site-header-address">{siteAddress}</Typography>
                </div>
                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img src={UsersIcon} alt="Users" style={{ width: '20px', height: '20px' }} />
                  <Typography style={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>
                    Total Online Users: <span style={{ color: '#1890ff', fontWeight: 600 }}>{onlineUsersCount}</span>
                  </Typography>
                </div>
              </div>
              <div className="site-status-indicator-box">
                <OnlineStatusIndicator isOnline={Boolean(enable)} />{' '}
                <span style={{ marginRight: '12px' }}>{enable ? 'Enable' : 'Disabled'}</span>
                {canEditSite && (
                  <Switch checked={switchChecked} onChange={handleToggleChange} disabled={toggleStatusLoading} />
                )}
              </div>
            </div>
          </Card>
        </Col>

        {/* Second Card End */}
      </Row>
    </>
  );
};
export default SingleSiteCard;
